import { useEffect, useRef, useState } from "react";
import { Eraser, Check, Loader2 } from "lucide-react";

/**
 * SignaturePad
 * -------------
 * Touch & mouse signature canvas. Returns a base64 PNG via onSave(base64).
 *
 * Props:
 *  - onSave(dataUrl, signerName)  → callback after user confirms
 *  - onCancel()                    → callback when user cancels
 *  - signerNameDefault             → optional default name
 *  - saving                        → external loading state
 */
export const SignaturePad = ({ onSave, onCancel, signerNameDefault = "", saving = false }) => {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const lastPosRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);
  const [name, setName] = useState(signerNameDefault);

  // Resize canvas to match its CSS box for crisp lines on retina.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      const ctx = canvas.getContext("2d");
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#09090B";
      ctx.lineWidth = 2.2;
      // White background so the PNG is readable on the PDF
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(0, 0, rect.width, rect.height);
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    const clientX = t ? t.clientX : e.clientX;
    const clientY = t ? t.clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true;
    lastPosRef.current = getPos(e);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const pos = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPosRef.current = pos;
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    drawingRef.current = false;
    lastPosRef.current = null;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasInk(false);
  };

  const confirm = () => {
    if (!hasInk) return;
    const dataUrl = canvasRef.current.toDataURL("image/png");
    onSave?.(dataUrl, name.trim() || null);
  };

  return (
    <div data-testid="signature-pad" className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 space-y-3">
      <div>
        <label className="block text-[12px] font-bold text-zinc-200 mb-1.5 uppercase tracking-wider">
          Name des Unterzeichners
        </label>
        <input
          data-testid="signature-name-input"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="z. B. Hans Mustermann"
          className="h-11 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-1.5">
          <label className="text-[12px] font-bold text-zinc-200 uppercase tracking-wider">
            Unterschrift
          </label>
          {hasInk && (
            <button
              type="button"
              onClick={clear}
              data-testid="signature-clear-btn"
              className="text-[12px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1 font-medium"
            >
              <Eraser size={13} /> Löschen
            </button>
          )}
        </div>
        <div className="rounded-xl border-2 border-dashed border-zinc-700 bg-white" style={{ touchAction: "none" }}>
          <canvas
            ref={canvasRef}
            data-testid="signature-canvas"
            className="w-full"
            style={{ height: "180px", display: "block", cursor: "crosshair" }}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
        </div>
        <p className="text-[12px] text-zinc-300 mt-1.5">
          Mit dem Bestätigen erklärt der Unterzeichner die Annahme der vorläufigen Kostenschätzung.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          disabled={saving}
          data-testid="signature-cancel-btn"
          className="h-12 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold text-[14px] transition active:scale-[0.97] disabled:opacity-50"
        >
          Abbrechen
        </button>
        <button
          type="button"
          onClick={confirm}
          disabled={!hasInk || saving}
          data-testid="signature-confirm-btn"
          className="h-12 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-[14px] hover:bg-[#00C964] transition active:scale-[0.97] disabled:opacity-40 inline-flex items-center justify-center gap-1.5"
        >
          {saving ? <Loader2 size={18} className="animate-spin" /> : <><Check size={16} /> Unterschreiben</>}
        </button>
      </div>
    </div>
  );
};
